/**
 * ChecklistService (formerly DeadlineChecklistService)
 *
 * This service handles checklist-, project-, and deliverable-related operations in the
 * application. It provides methods for managing checklist items and their associations
 * with matters, plus project design/table data and deliverables. The service supports
 * operations for both individual matters and system-wide management.
 *
 * @class
 */

import HttpService from './HttpService';
import type {
  CreateChecklistRequest,
  CreateChecklistResponse,
  EditChecklistItemResponse,
  GetChecklistItemsResponse,
  IncompleteChecklistItemsCountResponse,
  StatusChecklistItemsResponse,
  UpdateChecklistRequest,
} from '@frontend/Models/Checklist/ChecklistRequestResponseModels';
import {
  pathChecklistItems,
  pathChecklistByMatter,
  pathCountIncompleteChecklistItems,
  pathAddChecklistItem,
  pathEditChecklistItem,
  pathStatusChecklistItem,
  pathChecklistByMonth,
  pathAllChecklistItemsByMonth,
  pathProjectDesignData,
  pathProjectTableData,
  pathProjectTableDataById,
  pathProjectDesignDataById,
  pathDeliverablesByMatter,
  pathDeliverableById,
} from '@frontend/__settings__/septSoftwarePaths';
import {
  DeliverablesByDPIDPutResponse,
  DeliverablesByMatterIdGetResponse,
  ProjectDesignDataMatterIdTableResponse,
  ProjectDesignDataProjectIdPutResponse,
  ProjectTableDataMatterIdPutResponse,
  ProjectTableDataMatterIdResponse,
} from '@frontend/Models/Project/ProjectModels';

const http = new HttpService();

class ChecklistService {
  // Checklist Management Methods

  /**
   * Retrieves all checklist items from the system
   *
   * @returns {Promise<Array>} Array of checklist item objects
   * @throws {Error} If the API request fails
   */
  static async getAllChecklistItems(): Promise<GetChecklistItemsResponse> {
    const data = await http.get<GetChecklistItemsResponse>(pathChecklistItems);
    return data;
  }

  /**
   * Retrieves checklist items associated with a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Array>} Array of checklist items for the matter
   * @throws {Error} If the API request fails
   */
  static async getChecklistItemsByMatterId(
    matterId: string
  ): Promise<GetChecklistItemsResponse> {
    const data = await http.get<GetChecklistItemsResponse>(
      pathChecklistByMatter(matterId)
    );
    return data;
  }

  /**
   * Retrieves the count of incomplete checklist items
   *
   * @returns {Promise<number>} Count of incomplete checklist items
   * @throws {Error} If the API request fails
   */
  static async getIncompleteChecklistItemsCount(): Promise<IncompleteChecklistItemsCountResponse> {
    const data = await http.get<IncompleteChecklistItemsCountResponse>(
      pathCountIncompleteChecklistItems
    );
    return data;
  }

  /**
   * Adds a new checklist item to the system
   *
   * @param {Object} checklistData - The checklist item data to be created
   * @param {string} checklistData.title - Title of the checklist item
   * @param {string} [checklistData.description] - Description of the checklist item
   * @param {string} [checklistData.matterId] - Associated matter ID
   *
   * @returns {Promise<Object>} Created checklist item object
   * @throws {Error} If the API request fails
   */
  static async addChecklistItem(
    checklistData: CreateChecklistRequest
  ): Promise<CreateChecklistResponse> {
    const data = await http.post<CreateChecklistResponse>(
      pathAddChecklistItem,
      checklistData
    );
    return data;
  }

  /**
   * Updates an existing checklist item
   *
   * @param {string} itemId - ID of the checklist item to update
   * @param {Object} updatedFields - Fields to update
   * @returns {Promise<Object>} Updated checklist item object
   * @throws {Error} If the API request fails
   */
  static async editChecklistItem(
    itemId: string,
    updatedFields: UpdateChecklistRequest
  ): Promise<EditChecklistItemResponse> {
    const data = await http.put<EditChecklistItemResponse>(
      pathEditChecklistItem(itemId),
      updatedFields
    );
    return data;
  }

  /**
   * Updates the status of a checklist item
   *
   * @param {string} itemId - ID of the checklist item
   * @param {string} newStatus - New status to set
   * @returns {Promise<Object>} Updated checklist item object
   * @throws {Error} If the API request fails
   */
  static async updateChecklistItemStatus(
    itemId: string,
    newStatus: boolean
  ): Promise<StatusChecklistItemsResponse> {
    const data = await http.patch<StatusChecklistItemsResponse>(
      pathStatusChecklistItem(itemId),
      {
        status: newStatus,
      }
    );
    return data;
  }

  /**
   * Retrieves checklist items for a specific matter and month
   *
   * @param {string} matterId - ID of the matter
   * @param {string} month - Month to filter checklist items (format: YYYY-MM)
   * @returns {Promise<Array>} Array of checklist items for the specified month
   * @throws {Error} If the API request fails
   */
  static async getChecklistItemsByMonth(
    matterId: string,
    month: string
  ): Promise<GetChecklistItemsResponse> {
    const data = await http.get<GetChecklistItemsResponse>(
      pathChecklistByMonth(matterId, month)
    );
    return data;
  }

  /**
   * Retrieves all checklist items for a specific month
   *
   * @param {string} month - Month to filter checklist items (format: YYYY-MM)
   * @returns {Promise<Array>} Array of checklist items for the specified month
   * @throws {Error} If the API request fails
   */
  static async getAllChecklistItemsByMonth(
    month: string
  ): Promise<GetChecklistItemsResponse> {
    const data = await http.get<GetChecklistItemsResponse>(
      pathAllChecklistItemsByMonth(month)
    );
    return data;
  }

  /**
   * Retrieves project design data for a specific matter and table.
   * @param matterId the matter ID
   * @param tableName the tbale name
   * @returns
   */
  static async getProjectDesignData(
    matterId: string,
    tableName: string
  ): Promise<ProjectDesignDataMatterIdTableResponse> {
    const data = await http.get<ProjectDesignDataMatterIdTableResponse>(
      pathProjectDesignData(matterId, tableName)
    );
    return data;
  }

  /**
   * Retrieves project table data for a specific matter.
   *
   * @param matterId the matter ID
   * @returns
   */
  static async getProjectTableDataByMatterId(
    matterId: string
  ): Promise<ProjectTableDataMatterIdResponse> {
    const data = await http.get<ProjectTableDataMatterIdResponse>(
      pathProjectTableData(matterId)
    );
    return data;
  }

  /**
   * This updates the project table data
   *
   * @param pdId
   * @param updatedData
   * @returns
   */
  static async updateProjectTableData(
    pdId: string,
    updatedData: any
  ): Promise<ProjectTableDataMatterIdPutResponse> {
    const data = await http.put<ProjectTableDataMatterIdPutResponse>(
      pathProjectTableDataById(pdId),
      updatedData
    );
    return data;
  }

  static async updateProjectDesignData(
    pdId: string,
    updatedData: any
  ): Promise<ProjectDesignDataProjectIdPutResponse> {
    const data = await http.put<ProjectDesignDataProjectIdPutResponse>(
      pathProjectDesignDataById(pdId),
      updatedData
    );
    return data;
  }

  /**
   * Fetch all deliverables for a specific matterId.
   * GET /deliverables/:matterid
   */
  static async getDeliverablesByMatterId(
    matterId: string
  ): Promise<DeliverablesByMatterIdGetResponse[]> {
    const data = await http.get<DeliverablesByMatterIdGetResponse[]>(
      pathDeliverablesByMatter(matterId)
    );
    return data;
  }

  /**
   * Update a single deliverable by dp_id (primary key).
   * PUT /deliverables/:dp_id
   * Excludes updating matterid on the backend.
   */
  static async updateDeliverable(
    dpId: string,
    updatedData: any
  ): Promise<DeliverablesByDPIDPutResponse> {
    const data = await http.put<DeliverablesByDPIDPutResponse>(
      pathDeliverableById(dpId),
      updatedData
    );
    return data;
  }
}

export default ChecklistService;
